import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Skeleton
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const UserAnalyticsTab = ({ transactions, loading }) => {
    const { t } = useTranslation();

    // Prepare chart data
    const activityData = transactions.slice(0, 30).reverse().map((tx, index) => ({
        index: index + 1,
        deposit: tx.type === 'deposit' ? parseFloat(tx.amount / 1e11) : 0,
        withdrawal: tx.type === 'withdrawal' ? parseFloat(tx.amount / 1e11) : 0,
        amount: parseFloat(tx.amount / 1e11),
        type: tx.type,
        date: new Date(tx.time).toLocaleDateString(),
        time: new Date(tx.time).toLocaleString()
    }));

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {loading ? (
                    <Skeleton variant="text" width={200} height={28} />
                ) : (
                    t('user.activityAnalytics')
                )}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {loading ? (
                    <Skeleton variant="text" width={300} height={20} />
                ) : (
                    `${ t('user.recentTransactions') } - ${ t('user.transactionAmountsTrend') }`
                )}
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Skeleton variant="rectangular" width="100%" height={300} sx={{ mb: 2 }} />
                        <Skeleton variant="text" width={200} height={20} />
                    </Box>
                </Box>
            ) : (
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={activityData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis
                            dataKey="index"
                            label={{ value: t('user.transactionNumber'), position: 'insideBottom', offset: -5 }}
                            tick={{ fontSize: 12 }}
                        />
                        <YAxis
                            label={{ value: t('user.amount') + ' (BTC)', angle: -90, position: 'insideLeft' }}
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => value.toFixed(4)}
                        />
                        <Tooltip
                            formatter={(value, name) => [
                                `${ parseFloat(value).toFixed(8) } BTC`,
                                name === 'deposit' ? t('user.deposits') :
                                    name === 'withdrawal' ? t('user.withdrawals') : t('user.amount')
                            ]}
                            labelFormatter={(index) => {
                                const dataPoint = activityData[index - 1];
                                return dataPoint ? `${ t('user.transaction') } #${ index } - ${ dataPoint.time }` : `${ t('user.transaction') } #${ index }`;
                            }}
                            contentStyle={{
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                border: '1px solid #ccc',
                                borderRadius: '4px'
                            }}
                        />
                        <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconType="line"
                        />
                        <Line
                            type="monotone"
                            dataKey="deposit"
                            stroke="#4caf50"
                            strokeWidth={3}
                            dot={{ fill: '#4caf50', strokeWidth: 2, r: 5 }}
                            activeDot={{ r: 7, stroke: '#4caf50', strokeWidth: 2 }}
                            name={t('user.deposits')}
                            connectNulls={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="withdrawal"
                            stroke="#f44336"
                            strokeWidth={3}
                            dot={{ fill: '#f44336', strokeWidth: 2, r: 5 }}
                            activeDot={{ r: 7, stroke: '#f44336', strokeWidth: 2 }}
                            name={t('user.withdrawals')}
                            connectNulls={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            )}
        </Box>
    );
};

export default UserAnalyticsTab;
